/**
 * A document's history, in the side column beside Comments (RFC 0313 3g).
 *
 * Host-owned for the same reason Comments is: any document has a history, the
 * host owns its shape, and no app has to declare it. Every row is read from the
 * document's own change graph by `projectChanges`, so what this column says
 * and what the thread's card says can never disagree.
 *
 * Grouped by day and then by who, so ten minutes of typing is one group and
 * not forty rows. A row says what changed in a line: the text it took out and
 * put in, or the fields of a record it changed.
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo/slim'
import { Automerge as A } from '@automerge/automerge-repo/slim'
import { useRepo } from '@automerge/automerge-repo-react-hooks'
import {
  type CommentAuthor,
  copyFromVersion,
  type HistoryChange,
  projectAuthor,
  projectChanges,
  projectString,
  readDocumentContent,
  readVersions,
  restoreVersion,
  saveVersion,
  undoChange,
  type Version,
  versionsOf,
} from '@mod/core/model'
import { useMemo, useState } from 'react'
import { useBranchCopyOf } from '../../services/branches/branchSelection'
import {
  dayLabel,
  Face,
  fieldLine,
  RowAction,
  RowSummary,
  time,
} from '../../services/history/historyRows'
import { readLocalPerson } from '../../services/identity/localPerson'
import { sessionFace } from '../../services/sessions/sessionFace'
import { useDocument } from '../../services/world/repo/useDocument'
import type { VersionPreview } from './VersionPreview'

/** Rows read per page; Load older reads the next. */
const PAGE = 50

/** One day's worth of rows, split where the author changes. */
export interface HistoryDay {
  label: string
  /** Runs by one author, and saved versions as markers between them (3g). */
  groups: { key: string; rows: HistoryChange[]; version?: Version }[]
}

/** Who a row is by, as a key: a session, a person, or nobody stamped it. */
export function authorKey(row: HistoryChange): string {
  if (row.sessionId) return `s:${row.sessionId}`
  if (row.authorId) return `p:${row.authorId}`
  return 'none'
}

/** Rows (newest first) into days, and each day into runs by one author. Pure. */
export function groupHistory(
  rows: HistoryChange[],
  now: Date = new Date(),
  versions: Version[] = [],
): HistoryDay[] {
  // Rows and versions on one clock, newest first: a version sits in the list
  // at the moment it was saved.
  const items: (
    | { at: number; row: HistoryChange }
    | { at: number; version: Version }
  )[] = [
    ...rows.map((row) => ({ at: row.time, row })),
    ...versions.map((version) => ({ at: version.at / 1000, version })),
  ].sort((a, b) => b.at - a.at)
  const out: HistoryDay[] = []
  for (const item of items) {
    const label = dayLabel(item.at, now)
    let day = out[out.length - 1]
    if (!day || day.label !== label) {
      day = { label, groups: [] }
      out.push(day)
    }
    if ('version' in item) {
      day.groups.push({
        key: `v:${item.version.id}`,
        rows: [],
        version: item.version,
      })
      continue
    }
    // An undo or a restore stands alone: grouped with the same person's
    // typing it read as text they wrote, when it put back someone else's.
    const key =
      item.row.reverts || item.row.restores
        ? `act:${item.row.hash}`
        : authorKey(item.row)
    const last = day.groups[day.groups.length - 1]
    if (last && !last.version && last.key === key) last.rows.push(item.row)
    else day.groups.push({ key, rows: [item.row] })
  }
  return out
}

/**
 * Put the document back to just after this group (RFC 0313 3e). Pins the heads
 * the person was looking at: if anything arrived since, it says how much and
 * asks again rather than writing over it.
 */
function RestoreToHere({
  doc,
  heads,
  restore,
  label = 'Restore to here',
}: {
  doc: unknown
  heads: string[]
  label?: string
  restore: (
    heads: string[],
    seen: string[],
    force: boolean,
  ) => Promise<{ ok: boolean; since?: number }>
}) {
  const [busy, setBusy] = useState(false)
  const [moved, setMoved] = useState<{ since: number; seen: string[] } | null>(
    null,
  )
  const run = (force: boolean) => {
    const seen = moved?.seen ?? A.getHeads(doc as A.Doc<unknown>)
    setBusy(true)
    void restore(heads, seen, force)
      .then((out) => {
        if (!out.ok && out.since !== undefined) {
          setMoved({ since: out.since, seen })
        } else setMoved(null)
      })
      .catch(() => {})
      .finally(() => setBusy(false))
  }
  return (
    <button
      type="button"
      disabled={busy}
      onClick={() => run(moved !== null)}
      className="mt-1 rounded-full border border-border px-2 text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
    >
      {moved
        ? `${moved.since} change${moved.since === 1 ? '' : 's'} since you looked, restore anyway?`
        : label}
    </button>
  )
}

/**
 * What a document held at a version's heads, or null when it cannot be read
 * (a version from before the document existed, or heads this copy lacks).
 */
export function contentAt(
  doc: unknown,
  heads: string[] | undefined,
): string | null {
  if (!heads || !doc) return null
  try {
    return readDocumentContent(A.view(doc as A.Doc<unknown>, heads)) ?? null
  } catch {
    return null
  }
}

/**
 * A saved version, as a marker in the list (3g): dashed, with three moves.
 * View shows it without changing anything, in the document's pane when the
 * pane offers to (`onView`), else here; Open as a copy starts a new document
 * from it, beside this one, to explore another direction; Restore goes back
 * to it in place, saving what is here first (paired review of PR #329).
 */
function VersionMarker({
  doc,
  version,
  documentId,
  restore,
  openCopy,
  onView,
  showing,
}: {
  doc: unknown
  version: Version
  documentId: string
  restore: (
    heads: string[],
    seen: string[],
    force: boolean,
  ) => Promise<{ ok: boolean; since?: number }>
  openCopy?: (version: Version) => Promise<void>
  /** Show this version in the pane, or null to go back to current. */
  onView?: (preview: VersionPreview | null) => void
  /** True while the pane is showing this version. */
  showing?: boolean
}) {
  const heads = version.heads[documentId]
  const [inline, setInline] = useState(false)
  const viewing = onView ? Boolean(showing) : inline
  const then = useMemo(
    () => (inline ? contentAt(doc, heads) : null),
    [inline, heads, doc],
  )
  const toggle = () => {
    if (!onView) return setInline((v) => !v)
    if (showing) return onView(null)
    const content = contentAt(doc, heads)
    onView(
      content === null
        ? null
        : { id: version.id, name: version.name, at: version.at, content },
    )
  }
  return (
    <div className="mx-2 mb-2 rounded-lg border border-border border-dashed px-2.5 py-1.5 text-xs">
      <div className="flex items-center gap-2">
        <span className="text-muted-foreground">⚑</span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-foreground">{version.name}</span>
          <span className="block text-[11px] text-muted-foreground">
            Version · {version.by === 'auto' ? 'saved by Mod' : 'saved'} ·{' '}
            {time(version.at / 1000)}
          </span>
        </span>
        {heads ? (
          <>
            <button
              type="button"
              onClick={toggle}
              className="mt-1 rounded-full border border-border px-2 text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              {viewing ? 'Hide' : 'View'}
            </button>
            {openCopy ? (
              <button
                type="button"
                onClick={() => void openCopy(version)}
                className="mt-1 rounded-full border border-border px-2 text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                Open as a copy
              </button>
            ) : null}
            <RestoreToHere
              doc={doc}
              heads={heads}
              restore={restore}
              label="Restore"
            />
          </>
        ) : null}
      </div>
      {inline ? (
        <pre className="mt-1.5 max-h-60 overflow-auto whitespace-pre-wrap break-words rounded-md bg-muted/40 p-2 font-sans text-[11px] text-foreground/80">
          {then ?? 'This version cannot be read here.'}
        </pre>
      ) : null}
    </div>
  )
}

/** Save version: name the point you are at (3c). */
function SaveVersion({ save }: { save: (name: string) => Promise<unknown> }) {
  const [naming, setNaming] = useState(false)
  const [name, setName] = useState('')
  if (!naming) {
    return (
      <button
        type="button"
        onClick={() => setNaming(true)}
        className="rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        Save version
      </button>
    )
  }
  const done = () => {
    setNaming(false)
    setName('')
  }
  return (
    <form
      className="flex flex-1 items-center gap-1"
      onSubmit={(e) => {
        e.preventDefault()
        if (!name.trim()) return
        void save(name.trim()).finally(done)
      }}
    >
      <input
        // biome-ignore lint/a11y/noAutofocus: the field exists because they asked to name it
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => e.key === 'Escape' && done()}
        placeholder="Name this version"
        className="min-w-0 flex-1 rounded-md border border-border bg-transparent px-2 py-0.5 text-xs outline-none"
      />
    </form>
  )
}

export function HistorySection({
  projectId,
  documentId,
  onOpenSession,
  onOpenDocument,
  onViewVersion,
  viewingVersionId,
}: {
  projectId: string
  documentId: string
  /** Open the session a change came from (RFC 0313 3a). */
  onOpenSession?: (id: string) => void
  /** Open a document, here the copy made from a version. */
  onOpenDocument?: (id: string) => void
  /** Show a version in the document's pane, or null for current (3c). */
  onViewVersion?: (preview: VersionPreview | null) => void
  /** The version the pane is showing, if any. */
  viewingVersionId?: string | null
}) {
  const repo = useRepo() as unknown as Repo
  // RFC 0313 3j: on a branch the column reads and acts on the branch's copy,
  // which is what the editor beside it is showing.
  const branchCopy = useBranchCopyOf(projectId, documentId)
  const historyId = branchCopy ?? documentId
  const [doc] = useDocument<unknown>(historyId as AnyDocumentId)
  const [projectDoc] = useDocument(projectId as AnyDocumentId)
  const [pages, setPages] = useState(1)
  const restore = async (heads: string[], seen: string[], force: boolean) => {
    const person = readLocalPerson()
    const out = await restoreVersion(repo, projectId, historyId, heads, {
      seenHeads: seen,
      force,
      ...(person
        ? { authorId: person.id, by: { id: person.id, name: person.name } }
        : {}),
    })
    return out.ok
      ? { ok: true }
      : { ok: false, ...('since' in out ? { since: out.since } : {}) }
  }
  const undo = (hash: string) => {
    const person = readLocalPerson()
    return undoChange(repo, projectId, historyId, hash, {
      ...(person
        ? { authorId: person.id, by: { id: person.id, name: person.name } }
        : {}),
    })
  }

  // Re-read on every change to the document: `doc` is a new object per heads,
  // and the projection memoises its decode against that object.
  const page = useMemo(
    () => projectChanges(doc, { limit: PAGE * pages }),
    [doc, pages],
  )
  // RFC 0313 3c: the project's versions that hold this document.
  const versionsId = projectString(
    (projectDoc as { versionsDocId?: unknown } | undefined)?.versionsDocId,
  )
  const [versionsDoc] = useDocument<unknown>(
    (versionsId ?? null) as AnyDocumentId | null,
  )
  const versions = useMemo(
    // Versions are main's: a branch has no versions of its own (3c).
    () => (branchCopy ? [] : versionsOf(readVersions(versionsDoc), documentId)),
    [versionsDoc, documentId, branchCopy],
  )
  const days = useMemo(
    () => groupHistory(page.changes, new Date(), versions),
    [page, versions],
  )
  const save = (name: string) =>
    saveVersion(repo, projectId, {
      name,
      by: readLocalPerson()?.id ?? 'user',
      covers: { kind: 'document', documentId },
    })

  const authorOf = (row: HistoryChange): CommentAuthor | null => {
    if (!row.sessionId && !row.authorId) return null
    // A session's row wears the session's own mark, as its thread row does.
    return sessionFace(
      projectAuthor(projectDoc, {
        author: row.authorId ?? '',
        ...(row.sessionId ? { authorSessionId: row.sessionId } : {}),
      }),
    )
  }

  if (!doc) {
    return <p className="p-3 text-muted-foreground text-xs">Loading history…</p>
  }
  if (!page.changes.length) {
    return (
      <p className="p-3 text-muted-foreground text-xs">
        No changes recorded yet.
      </p>
    )
  }
  return (
    <div className="flex h-full min-h-0 flex-col overflow-y-auto pb-3">
      <div className="flex items-center justify-end gap-2 px-3 pt-2">
        {branchCopy ? (
          <span className="mr-auto text-[11px] text-muted-foreground">
            On this window's branch
          </span>
        ) : (
          <SaveVersion save={save} />
        )}
      </div>
      {days.map((day) => (
        <section key={day.label}>
          <h3 className="px-3 pt-3 pb-1 text-[11px] text-muted-foreground">
            {day.label}
          </h3>
          {day.groups.map((g) => {
            if (g.version) {
              return (
                <VersionMarker
                  key={g.key}
                  doc={doc}
                  version={g.version}
                  documentId={documentId}
                  restore={restore}
                  {...(onViewVersion
                    ? {
                        onView: onViewVersion,
                        showing: viewingVersionId === g.version.id,
                      }
                    : {})}
                  openCopy={async (v) => {
                    const id = await copyFromVersion(
                      repo,
                      projectId,
                      documentId,
                      v,
                    )
                    if (id) onOpenDocument?.(id)
                  }}
                />
              )
            }
            const first = g.rows[0] as HistoryChange
            const author = authorOf(first)
            return (
              <div
                key={`${day.label}-${first.hash}`}
                className="mx-2 mb-2 rounded-lg border border-border/70 bg-card px-2.5 py-2"
              >
                <div className="mb-1 flex items-center gap-1.5 text-xs">
                  <Face author={author} />
                  <span className="truncate text-foreground">
                    {author?.label ?? 'Unattributed'}
                  </span>
                  <span className="shrink-0 text-[11px] text-muted-foreground">
                    {time(first.time)}
                    {g.rows.length > 1 ? ` · ${g.rows.length} changes` : ''}
                  </span>
                </div>
                {g.rows.map((row) => (
                  <div
                    key={row.hash}
                    className="flex items-start gap-2 py-0.5 text-xs"
                    data-change={row.hash}
                  >
                    <span className="min-w-0 flex-1 break-words">
                      <RowSummary row={row} />
                      {row.note && (
                        <span className="block text-[11px] text-muted-foreground">
                          {row.note}
                        </span>
                      )}
                      {/* RFC 0313 3a: an agent's change opens the conversation
                          that made it, so "why did this change" is one click. */}
                      {row.sessionId && onOpenSession ? (
                        <button
                          type="button"
                          onClick={() => onOpenSession(row.sessionId as string)}
                          className="block text-[11px] text-muted-foreground hover:text-foreground hover:underline"
                        >
                          {row.turnId
                            ? 'Why: open the turn'
                            : 'Open the session'}
                        </button>
                      ) : null}
                    </span>
                    <RowAction doc={doc} row={row} undo={undo} />
                  </div>
                ))}
                {/* Not on the newest group: it already is the document now. */}
                {first.hash !== page.changes[0]?.hash && (
                  <RestoreToHere
                    doc={doc}
                    heads={[first.hash]}
                    restore={restore}
                  />
                )}
              </div>
            )
          })}
        </section>
      ))}
      {page.more && (
        <button
          type="button"
          onClick={() => setPages((n) => n + 1)}
          className="mx-3 mt-1 self-start rounded-full border border-border px-2.5 py-0.5 text-[11px] text-muted-foreground hover:text-foreground"
        >
          Load older
        </button>
      )}
    </div>
  )
}

export { fieldLine }
